import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Building2, KeyRound } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type * as S from "@releo/shared";
import { Button, ErrorBox, Field, Input, Spinner } from "@/components/ui";
import { http } from "@/lib/api";
import { keys, useMe } from "@/lib/queries";
import { AuthCard, startSso } from "./auth";

export function InvitationPage({ id }: { id: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe();
  const invitation = useQuery({ queryKey: ["invitation", id], queryFn: () => http.get<S.PublicInvitation>(`/invitations/${id}`), retry: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const joined = async (user: S.Me) => {
    qc.setQueryData(keys.me, user);
    await qc.resetQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    await navigate({ to: "/apps" });
  };
  const run = async (action: () => Promise<S.Me>) => {
    setBusy(true);
    setError(null);
    try {
      await joined(await action());
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const signOut = async () => {
    setBusy(true);
    try {
      await http.post("/auth/logout");
      qc.setQueryData(keys.me, null);
      await qc.resetQueries({ predicate: (q) => q.queryKey[0] !== "me" && q.queryKey[0] !== "invitation" });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const continueWithSso = async (email: string) => {
    setBusy(true);
    setError(null);
    try {
      window.location.assign(await startSso(email, `/invitations/${id}`));
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  if (invitation.isLoading || me.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  const inv = invitation.data;
  if (!inv) {
    return (
      <AuthCard title={t("invitation.title")}>
        <ErrorBox error={new Error(t("invitation.notFound"))} />
      </AuthCard>
    );
  }
  const role = t({ owner: "org.roleOwner", admin: "org.roleAdmin", member: "org.roleMember" }[inv.role]).toLowerCase();
  const subtitle = inv.inviter
    ? t("invitation.subtitle", { inviter: inv.inviter.name, organization: inv.organization.name, role })
    : t("invitation.subtitleNoInviter", { organization: inv.organization.name, role });

  let body: React.ReactNode;
  if (inv.status !== "pending") {
    body = <ErrorBox error={new Error(t(`invitation.${inv.status}`))} />;
  } else if (me.data) {
    body =
      me.data.email.toLowerCase() === inv.email ? (
        <Button className="w-full justify-center" loading={busy} onClick={() => void run(() => http.post<S.Me>(`/invitations/${id}/accept`))}>
          {t("invitation.join", { organization: inv.organization.name })}
        </Button>
      ) : (
        <div className="space-y-3">
          <ErrorBox error={new Error(t("invitation.wrongAccount", { email: inv.email, current: me.data.email }))} />
          <Button variant="outline" className="w-full justify-center" loading={busy} onClick={() => void signOut()}>
            {t("auth.logout")}
          </Button>
        </div>
      );
  } else if (inv.ssoRequired) {
    body = (
      <Button className="w-full justify-center" loading={busy} onClick={() => void continueWithSso(inv.email)}>
        <KeyRound className="h-4 w-4" />
        {t("auth.ssoContinue")}
      </Button>
    );
  } else if (inv.accountExists) {
    body = (
      <Link to="/login" search={{ redirect: `/invitations/${id}` } as never} className="block">
        <Button className="w-full justify-center">{t("invitation.signInToJoin")}</Button>
      </Link>
    );
  } else {
    const submit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const data = Object.fromEntries(new FormData(e.currentTarget));
      void run(() => http.post<S.Me>(`/invitations/${id}/signup`, data));
    };
    body = (
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("common.email")} htmlFor="inv-email">
          <Input id="inv-email" value={inv.email} disabled readOnly />
        </Field>
        <Field label={t("common.name")} htmlFor="inv-name">
          <Input id="inv-name" name="name" required autoComplete="name" autoFocus />
        </Field>
        <Field label={t("common.password")} help={t("auth.passwordHint")} htmlFor="inv-password">
          <Input id="inv-password" name="password" type="password" minLength={10} required autoComplete="new-password" />
        </Field>
        <Button type="submit" className="w-full justify-center" loading={busy}>
          {t("invitation.createAndJoin")}
        </Button>
      </form>
    );
  }

  return (
    <AuthCard title={t("invitation.title")} subtitle={subtitle}>
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <Building2 className="h-4 w-4 text-fg-muted" />
          <span className="font-medium">{inv.organization.name}</span>
        </div>
        {body}
        <ErrorBox error={error} />
      </div>
    </AuthCard>
  );
}
